import { useEffect, useState } from "react";
import { useClasses } from "@/lib/activeClass";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, UserCircle, BookOpen, ClipboardList, CalendarCheck2, Clock, UserX,
  Award, Sparkles, ThumbsUp, ShieldAlert, AlertOctagon, Bell, TrendingUp,
  TrendingDown, Activity, Star, ChevronRight,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend,
} from "recharts";

const PALETTE = {
  green: "#1F7A63",
  greenDark: "#155744",
  yellow: "#E6B800",
  blue: "#2563EB",
  red: "#D93838",
  light: "#E8F2EF",
  grey: "#5C736A",
};

export default function TeacherDashboard({ userName }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const { classes, classId } = useClasses();

  useEffect(() => {
    if (!classId && classes.length) return;
    setD(null);
    api.get("/dashboard/maitre", { params: { class_id: classId || undefined } })
      .then(({ data }) => setD(data))
      .catch((e) => { setErr(formatError(e)); toast.error(formatError(e)); });
  }, [classId, classes.length]);

  if (err) return <div className="ek-card p-6 text-red-600 text-sm">{err}</div>;
  if (!d) return (
    <div className="space-y-4">
      <div className="skel h-32" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{Array.from({length:8}).map((_,i)=><div key={i} className="skel h-24"/>)}</div>
      <div className="skel h-64" />
    </div>
  );

  const { meta, today, vie_scolaire, performance, summary, top_students, to_watch, presence_week, evolution_moyennes, recent, notifications } = d;
  const gender = [
    { name: "Garçons", value: meta.garcons, color: PALETTE.blue },
    { name: "Filles", value: meta.filles, color: PALETTE.yellow },
  ];
  const presenceSlice = [
    { name: "Présents", value: today.presents, color: PALETTE.green },
    { name: "Absents", value: today.absents, color: PALETTE.red },
    { name: "Retards", value: today.retards, color: PALETTE.yellow },
  ].filter((x) => x.value > 0);

  return (
    <div className="space-y-5">
      {/* Welcome */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>
            Bonjour, {userName?.split(" ")[0]} 👋
          </h1>
          <p className="text-sm text-[#5C736A] mt-1">
            Voici l&apos;état de {classes.length > 1 ? <>la classe <b className="text-[#1F7A63]">{classes.find((c) => c.id === classId)?.nom}</b></> : "votre classe"} aujourd&apos;hui.
          </p>
        </div>
      </div>

      {/* PERFORMANCE INDEX — Hero card */}
      <PerformanceHero performance={performance} summary={summary} />

      {/* Stats grid — 8 mini cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat icon={UserCircle} tone="green" label="Élèves" value={meta.eleves_total} sub={`${meta.garcons}G · ${meta.filles}F`} />
        <MiniStat icon={CalendarCheck2} tone="green" label="Présents aujourd&apos;hui" value={today.presents} sub={`sur ${meta.eleves_total}`} />
        <MiniStat icon={UserX} tone="red" label="Absents" value={today.absents} sub={`+${today.retards} retards`} />
        <MiniStat icon={BookOpen} tone="slate" label="Matières" value={meta.matieres} />
        <MiniStat icon={ClipboardList} tone="yellow" label="Notes saisies (aujourd&apos;hui)" value={today.notes_saisies} />
        <MiniStat icon={ThumbsUp} tone="green" label="Félicitations" value={vie_scolaire.felicitations} sub="module à venir" />
        <MiniStat icon={ShieldAlert} tone="yellow" label="Sanctions" value={vie_scolaire.sanctions} sub="module à venir" />
        <MiniStat icon={AlertOctagon} tone="red" label="Incidents" value={vie_scolaire.incidents} sub="module à venir" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Présences (7 derniers jours)" icon={CalendarCheck2}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={presence_week}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="jour" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="presents" fill={PALETTE.green} name="Présents" radius={[4, 4, 0, 0]} />
              <Bar dataKey="absents" fill={PALETTE.red} name="Absents" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Répartition Garçons / Filles" icon={Users}>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={gender} dataKey="value" nameKey="name" outerRadius={70} label>
                {gender.map((g, i) => <Cell key={i} fill={g.color} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Évolution moyenne classe" icon={TrendingUp}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={evolution_moyennes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="trimestre" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="moyenne" stroke={PALETTE.green} strokeWidth={3}
                    dot={{ fill: PALETTE.green, r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Two-column: top + to-watch */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SectionCard title="Meilleurs élèves" icon={Award} color={PALETTE.green}>
          {top_students.length === 0 ? (
            <EmptyRow text="Pas encore de notes." />
          ) : (
            <div className="space-y-2">
              {top_students.map((s, i) => (
                <div key={s.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                  <div className={`w-8 h-8 rounded-full grid place-items-center text-white text-xs font-bold ${
                    i === 0 ? "bg-[#E6B800]" : i === 1 ? "bg-slate-400" : i === 2 ? "bg-[#b87333]" : "bg-slate-300"
                  }`}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate text-sm"><span className="font-normal">{s.prenom}</span> {s.nom}</div>
                    <div className="text-xs text-[#5C736A] font-mono">{s.matricule}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-[#1F7A63]" style={{ fontFamily: "Outfit" }}>{s.moyenne.toFixed(2)}</div>
                    <div className="text-[10px] text-[#5C736A]">/10</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Élèves à suivre" icon={AlertOctagon} color={PALETTE.red}>
          {to_watch.length === 0 ? (
            <EmptyRow text="Aucun élève ne nécessite d&apos;attention particulière." />
          ) : (
            <div className="space-y-2">
              {to_watch.map((s) => (
                <div key={s.id} className="p-2 rounded-lg border border-red-100 bg-red-50/40">
                  <div className="flex items-start gap-2">
                    <TrendingDown className="w-4 h-4 text-red-500 shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate"><span className="font-normal">{s.prenom}</span> {s.nom}</div>
                      <div className="text-xs text-red-700">{s.reasons.join(" · ")}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Activities + Notifications */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SectionCard title="Activités récentes" icon={Activity} color={PALETTE.green}>
          {recent.length === 0 ? (
            <EmptyRow text="Aucune activité récente." />
          ) : (
            <div className="space-y-2">
              {recent.map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-sm py-1.5 border-b border-slate-100 last:border-0">
                  <ActivityIcon type={r.type} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{r.text}</div>
                    {r.at && <div className="text-[10px] text-[#5C736A]">{new Date(r.at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Notifications" icon={Bell} color={PALETTE.yellow}>
          {notifications.length === 0 ? (
            <EmptyRow text="Aucune notification. Tout est à jour !" />
          ) : (
            <div className="space-y-2">
              {notifications.map((n, i) => (
                <div key={i} className={`p-2 rounded-lg text-sm flex items-start gap-2 ${
                  n.level === "warn" ? "bg-red-50 text-red-800" :
                  n.level === "success" ? "bg-emerald-50 text-emerald-800" :
                  "bg-blue-50 text-blue-800"
                }`}>
                  <Bell className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{n.text}</span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {presenceSlice.length > 0 && (
        <SectionCard title="Répartition présences (aujourd&apos;hui)" icon={UserCircle} color={PALETTE.blue}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
            <div className="h-40">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={presenceSlice} dataKey="value" nameKey="name" outerRadius={60} label>
                    {presenceSlice.map((g, i) => <Cell key={i} fill={g.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 text-sm">
              {presenceSlice.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ background: p.color }} />
                  <span>{p.name}</span>
                  <span className="ml-auto font-bold">{p.value}</span>
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

/* ============ Sub-components ============ */
function PerformanceHero({ performance, summary }) {
  const { score, stars, label, color } = performance;
  return (
    <div className="ek-card p-5 sm:p-6 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${color}15, ${color}05)` }}>
      <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ background: `radial-gradient(circle at 100% 0%, ${color}, transparent 60%)` }} />
      <div className="relative flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="shrink-0">
          <div className="text-xs uppercase tracking-wider text-[#5C736A]">Indice de performance de la classe</div>
          <div className="mt-1 flex items-baseline gap-1" style={{ fontFamily: "Outfit" }}>
            <span className="text-5xl sm:text-6xl font-bold" style={{ color }}>{score != null ? score : "—"}</span>
            <span className="text-lg text-[#5C736A]">/100</span>
          </div>
          <div className="flex items-center gap-1 mt-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className="w-5 h-5" fill={n <= stars ? "#E6B800" : "none"} stroke="#E6B800" strokeWidth={2} />
            ))}
            <span className="ml-2 text-sm font-semibold" style={{ color }}>{label}</span>
          </div>
        </div>
        <div className="flex-1 md:pl-6 md:border-l md:border-slate-200">
          <div className="flex items-center gap-2 text-sm font-semibold mb-1" style={{ fontFamily: "Outfit" }}>
            <Sparkles className="w-4 h-4 text-[#E6B800]" /> Résumé de ma classe aujourd&apos;hui
          </div>
          <p className="text-sm text-[#1A2E28] leading-relaxed">{summary}</p>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, sub, tone = "slate" }) {
  const tones = {
    green: "bg-[#E8F2EF] text-[#1F7A63]",
    yellow: "bg-[#FCF8E6] text-[#b38f00]",
    red: "bg-[#fbebeb] text-[#D93838]",
    blue: "bg-blue-50 text-blue-700",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div className="ek-card p-3 sm:p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs text-[#5C736A] uppercase tracking-wide truncate">{label}</div>
          <div className="text-xl sm:text-2xl font-bold mt-0.5" style={{ fontFamily: "Outfit" }}>{value ?? "—"}</div>
          {sub && <div className="text-[10px] text-[#5C736A] mt-0.5 truncate">{sub}</div>}
        </div>
        <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${tones[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, icon: Icon, children }) {
  return (
    <div className="ek-card p-4">
      <div className="flex items-center gap-2 mb-3 text-sm font-semibold" style={{ fontFamily: "Outfit" }}>
        <Icon className="w-4 h-4 text-[#1F7A63]" /> {title}
      </div>
      {children}
    </div>
  );
}

function SectionCard({ title, icon: Icon, color, children }) {
  return (
    <div className="ek-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold" style={{ fontFamily: "Outfit" }}>
          <Icon className="w-4 h-4" style={{ color }} /> {title}
        </div>
      </div>
      {children}
    </div>
  );
}

function EmptyRow({ text }) {
  return <div className="text-center text-xs text-[#5C736A] py-4">{text}</div>;
}

function ActivityIcon({ type }) {
  const map = {
    absence: <UserX className="w-4 h-4 text-red-500" />,
    retard: <Clock className="w-4 h-4 text-yellow-600" />,
    note: <ClipboardList className="w-4 h-4 text-[#1F7A63]" />,
    evaluation: <Award className="w-4 h-4 text-blue-600" />,
    felicitation: <ThumbsUp className="w-4 h-4 text-emerald-600" />,
  };
  return <div className="shrink-0 mt-0.5">{map[type] || <ChevronRight className="w-4 h-4 text-[#5C736A]" />}</div>;
}
